"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Vector = void 0;
var Vector = /** @class */ (function () {
    function Vector(x, y, z) {
        this.x = x || 0;
        this.y = y || 0;
        this.z = z || 0;
    }
    Vector.prototype.negative = function () {
        return new Vector(-this.x, -this.y, -this.z);
    };
    Vector.prototype.add = function (v) {
        if (v instanceof Vector) {
            return new Vector(this.x + v.x, this.y + v.y, this.z + v.z);
        }
        return new Vector(this.x + v, this.y + v, this.z + v);
    };
    Vector.prototype.subtract = function (v) {
        if (v instanceof Vector) {
            return new Vector(this.x - v.x, this.y - v.y, this.z - v.z);
        }
        return new Vector(this.x - v, this.y - v, this.z - v);
    };
    Vector.prototype.multiply = function (v) {
        if (v instanceof Vector) {
            return new Vector(this.x * v.x, this.y * v.y, this.z * v.z);
        }
        return new Vector(this.x * v, this.y * v, this.z * v);
    };
    Vector.prototype.divide = function (v) {
        if (v instanceof Vector) {
            return new Vector(this.x / v.x, this.y / v.y, this.z / v.z);
        }
        return new Vector(this.x / v, this.y / v, this.z / v);
    };
    Vector.prototype.equals = function (v) {
        return this.x === v.x && this.y === v.y && this.z === v.z;
    };
    Vector.prototype.dot = function (v) {
        return this.x * v.x + this.y * v.y + this.z * v.z;
    };
    Vector.prototype.cross = function (v) {
        return new Vector(this.y * v.z - this.z * v.y, this.z * v.x - this.x * v.z, this.x * v.y - this.y * v.x);
    };
    Vector.prototype.length = function () {
        return Math.sqrt(this.dot(this));
    };
    Vector.prototype.unit = function () {
        return this.divide(this.length());
    };
    Vector.prototype.min = function () {
        return Math.min(Math.min(this.x, this.y), this.z);
    };
    Vector.prototype.max = function () {
        return Math.max(Math.max(this.x, this.y), this.z);
    };
    Vector.prototype.toAngles = function () {
        return {
            theta: Math.atan2(this.z, this.x),
            phi: Math.asin(this.y / this.length())
        };
    };
    Vector.prototype.round = function (factor) {
        if (factor === void 0) { factor = 100; }
        var vect = this.clone();
        if (vect.x !== 0) {
            vect.x = Math.round(vect.x * factor) / factor;
        }
        if (vect.y !== 0) {
            vect.y = Math.round(vect.y * factor) / factor;
        }
        if (vect.z !== 0 && vect.z != null) {
            vect.z = Math.round(vect.z * factor) / factor;
        }
        return vect;
    };
    Vector.prototype.norm = function () {
        var pow = Math.pow(this.x, 2) + Math.pow(this.y, 2) + Math.pow(this.z || 0, 2);
        return Math.sqrt(pow);
    };
    Vector.prototype.normalize = function () {
        var norm = this.norm();
        if (norm > 1e-12) {
            return new Vector(this.x / norm, this.y / norm, this.z / norm);
        }
        return this.clone();
    };
    Vector.prototype.angleTo = function (a) {
        return Math.acos(this.dot(a) / (this.length() * a.length()));
    };
    Vector.prototype.toArray = function (n) {
        if (n === void 0) { n = 3; }
        return [this.x, this.y, this.z].slice(0, n || 3);
    };
    Vector.prototype.clone = function () {
        return new Vector(this.x, this.y, this.z);
    };
    Vector.prototype.init = function (x, y, z) {
        this.x = x;
        this.y = y;
        this.z = z;
        return this;
    };
    Vector.create = function (obj) {
        if (obj instanceof Vector) {
            return obj;
        }
        if (Array.isArray(obj)) {
            return new Vector(obj[0], obj[1], obj[2]);
        }
        return new Vector(obj.x, obj.y, obj.z);
    };
    /**
     * Makes a (x,y,z) Cartesian vector out of length, bearing and pitch
     * <p/>
     * As per Spike tests
     * y point North, x points East, z point Zenith
     *
     * @param length  in meters
     * @param bearing East (bearing) azimuth in radian (East is pi/2)
     * @param pitch   in radians (Zenith is pi/2)
     * @return Cartesian (x,y,z) vector
     */
    Vector.distanceBearingPitchToCartesian = function (length, bearing, pitch) {
        var yxProjection = length * Math.cos(pitch);
        return new Vector(yxProjection * Math.sin(bearing), yxProjection * Math.cos(bearing), length * Math.sin(pitch));
    };
    /** Makes a triplet (length, bearing, pitch) out of a a (x,y,z) Cartesian vector
     *
     * @param w (x,y,z) Cartesian vector
     * @return (length, bearing, pitch) representation
     */
    Vector.distanceBearingAndPitchFromCartesian = function (w) {
        var length = w.norm();
        var pitch = 0.0;
        var bearing = 0.0;
        if (length > 1e-12) {
            pitch = Math.asin(w.z / length);
            if (w.y !== 0) {
                bearing = Math.atan2(w.x, w.y);
            }
            else {
                if (w.x > 0) {
                    bearing = Math.PI / 2.0;
                }
                else if (w.x < 0) {
                    bearing = -Math.PI / 2.0;
                }
            }
        }
        return new Vector(length, bearing, pitch);
    };
    Vector.prototype.toString = function () {
        return '(' + this.x + ', ' + this.y + ', ' + this.z + ')';
    };
    Vector.prototype.toXYString = function () {
        return this.x + ', ' + this.y;
    };
    Vector.prototype.inverseRotate = function (rotation) {
        return new Vector(this.x * rotation[0] + this.y * rotation[3] + this.z * rotation[6], this.x * rotation[1] + this.y * rotation[4] + this.z * rotation[7], this.x * rotation[2] + this.y * rotation[5] + this.z * rotation[8]);
    };
    Vector.prototype.rotate = function (rotation) {
        return new Vector(this.x * rotation[0] + this.y * rotation[1] + this.z * rotation[2], this.x * rotation[3] + this.y * rotation[4] + this.z * rotation[5], this.x * rotation[6] + this.y * rotation[7] + this.z * rotation[8]);
    };
    Vector.prototype.approx = function (v) {
        return approx(this.x, v.x) && approx(this.y, v.y) && approx(this.z, v.z);
    };
    return Vector;
}());
exports.Vector = Vector;
function approx(a, b) {
    return Math.abs(a - b) < 1e-12;
}
//# sourceMappingURL=vector.js.map